import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
  BehaviorSubject,
  concatMap,
  map,
  of,
  tap,
  catchError,
  delay,
} from 'rxjs'
import { Observable } from 'rxjs'
import { environment } from 'src/environments/environment'
import { jwtDecode } from 'jwt-decode'

const DEBUG = environment.logLevel === 'debug'
import { H2HProfile } from '../types/profile'
import { USER_INTERESTS } from 'src/app/constants/profile'
import { H2HProfileWithRole, H2HRole, H2HUserRole } from '../types/role'
import { H2HPet } from '../types/pet'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isAuthenticated$ = new BehaviorSubject<boolean>(false)
  public isSuperAdmin$ = new BehaviorSubject<boolean>(false)
  public isShelterAdmin$ = new BehaviorSubject<boolean>(false)
  public verified$ = new BehaviorSubject<boolean | null>(null)
  public interests$ = new BehaviorSubject<string | null>(null)
  public user$ = new BehaviorSubject<{ name: string } | null>(null)
  public userCreatedAt$ = new BehaviorSubject<string | null>(null) // Added to track creation date
  public userProfileLoaded$ = new BehaviorSubject<boolean>(false) // Track when user profile is loaded

  private AUTH_API_BASE_URL = environment.apiBaseUrl
  private http = inject(HttpClient)
  private router = inject(Router)

  constructor() {
    this.updateIsAuthenticated()
    this.updateIsSuperAdmin()
    this.updateIsShelterAdmin()
    this.updateVerified()
    this.updateInterests()
    this.updateUser()
    this.updateUserCreatedAt() // Initialize user creation date
  }

  get accessToken() {
    return localStorage.getItem('accessToken')
  }

  get idToken() {
    return localStorage.getItem('idToken')
  }

  get refreshToken() {
    return localStorage.getItem('refreshToken')
  }

  private get userInfo(): GetMyUserRolesResp | null {
    return JSON.parse(localStorage.getItem('user') ?? 'null')
  }

  get IMPAccessToken() {
    return localStorage.getItem('IMP::accessToken')
  }

  get IMPIdToken() {
    return localStorage.getItem('IMP::idToken')
  }

  private get IMPuserInfo(): GetMyUserRolesResp | null {
    return JSON.parse(localStorage.getItem('IMP::user') ?? 'null')
  }

  getCurrentUserInfo(): GetMyUserRolesResp | null {
    return this.IMPuserInfo || this.userInfo
  }

  get isSuperAdmin() {
    return this.isSuperAdmin$.value
  }

  get isShelterAdmin() {
    return this.isShelterAdmin$.value
  }

  get isAdmin() {
    return this.isSuperAdmin || this.isShelterAdmin
  }

  private updateIsSuperAdmin() {
    const user = this.getCurrentUserInfo()
    // Check both conditions: isGlobal OR UUID matching
    const isGlobalAdmin = user?.role?.isGlobal ?? false
    const hasMatchingUuid = user?.role?.uuid === environment.adminRoleUuid.super

    // Set as super admin if either condition is true
    this.isSuperAdmin$.next(isGlobalAdmin || hasMatchingUuid)

    // Update verification status when admin status changes
    this.updateVerified()
  }

  private updateIsShelterAdmin() {
    const user = this.getCurrentUserInfo()
    this.isShelterAdmin$.next(user?.userRole?.resourceId !== undefined)

    // Update verification status when admin status changes
    this.updateVerified()
  }

  private decodeToken(token: string) {
    return jwtDecode(token)
  }

  isAuthValid(): boolean {
    if (!this.accessToken) {
      return false
    }
    const expiresAt = this.decodeToken(this.accessToken).exp
    return (
      !!this.accessToken &&
      !!this.idToken &&
      !!expiresAt &&
      Date.now() / 1000 < expiresAt
    )
  }

  currentIdTokenProfile() {
    const token = this.IMPIdToken || this.idToken
    if (token) {
      const payload = this.decodeToken(token)

      // Check if we have a locally stored verification status
      const storedVerificationStatus = sessionStorage.getItem(
        'h2h_verification_status',
      )

      // If we have a stored verification status, override the token data
      if (storedVerificationStatus) {
        return {
          ...payload,
          'custom:verified': storedVerificationStatus,
        } as any
      }

      return payload as any
    }
    return null
  }

  private updateIsAuthenticated() {
    this.isAuthenticated$.next(this.isAuthValid())
  }

  private updateVerified() {
    // If verification is disabled, always set verified to false
    if (!this.isVerificationEnabled) {
      this.verified$.next(false)
      return
    }

    // Check for admin override first
    const adminOverride = localStorage.getItem('admin_verification_override')
    if (adminOverride !== null) {
      this.verified$.next(adminOverride === 'true')
      return
    }

    const profile = this.currentIdTokenProfile()
    const verified = profile?.['custom:verified']

    // Only consider users verified if they have a non-empty custom:verified attribute
    // Special case: also consider valid if it contains 'shelterAdmin' or 'superAdmin'
    let isVerified = false

    if (verified && verified !== '') {
      // Try to parse as JSON - might be the new format
      try {
        const parsedVerified = JSON.parse(verified)
        // If it has a session property, it's valid
        isVerified = !!parsedVerified.session
      } catch (e) {
        // Not JSON, just a string (old format) - still valid
        isVerified = true
      }
    }

    // Clear any lingering verification data if the token shows the user is not verified
    // This handles cases where sessionStorage/localStorage has stale verification data
    if (!isVerified) {
      // Clear our custom verification status
      sessionStorage.removeItem('h2h_verification_status')

      // Also clear any related verification data from localStorage
      localStorage.removeItem('verification_session_id')
    }

    this.verified$.next(isVerified)
  }

  /**
   * Calls the backend API to check verification status
   * This is useful when needing server-side confirmation of verification
   * status that can't be tampered with on the client
   */
  checkVerificationStatus(): Observable<boolean> {
    // If verification is disabled, always return false
    if (!this.isVerificationEnabled) {
      return of(false)
    }

    return this.http
      .get<{ verified: boolean }>(`${this.AUTH_API_BASE_URL}/auth/verify`)
      .pipe(map((response) => response.verified))
  }

  private updateInterests() {
    const profile = this.currentIdTokenProfile()
    const interests = profile?.['custom:interests'] || null
    this.interests$.next(interests)
  }

  private updateUser() {
    const profile = this.currentIdTokenProfile()
    const userInfo = this.getCurrentUserInfo()
    const name =
      `${userInfo?.user?.firstName || ''} ${userInfo?.user?.lastName || ''}`.trim() ||
      profile?.name ||
      'Your Account'
    this.user$.next({ name })
  }

  private updateUserCreatedAt() {
    const profile = this.currentIdTokenProfile()
    // Use 'iat' (issued at) as a proxy for creation date if 'custom:created_at' isn't available
    const customCreatedAt = profile?.['custom:created_at']
    const iatTimestamp = profile?.iat
      ? new Date(profile.iat * 1000).toISOString()
      : null

    if (DEBUG) console.log('ID token profile:', profile)
    if (DEBUG) console.log('custom:created_at from token:', customCreatedAt)
    if (DEBUG)
      console.log(
        'iat from token:',
        profile?.iat,
        'converted to ISO:',
        iatTimestamp,
      )

    const createdAt = customCreatedAt || iatTimestamp
    if (DEBUG) console.log('Final createdAt value:', createdAt)

    this.userCreatedAt$.next(createdAt)
  }

  getUuid() {
    const token = this.IMPIdToken || this.idToken
    if (!token) {
      return null
    }
    return jwtDecode(token).sub
  }

  getUserCreatedAt(): string | null {
    return this.userCreatedAt$.value
  }

  register(body: RegisterBody) {
    return this.http.post<RegisterResponse>(
      `${this.AUTH_API_BASE_URL}/v2/auth/register`,
      body,
    )
  }

  confirm(body: ConfirmBody) {
    return this.http.post(`${this.AUTH_API_BASE_URL}/auth/confirm`, body)
  }

  resendCode(email: string) {
    return this.http.post(`${this.AUTH_API_BASE_URL}/auth/resend-code`, {
      email: email,
    })
  }

  assignRole(assignRoleBody: AssignRoleBody) {
    return this.http.post<H2HUserRole>(
      `${this.AUTH_API_BASE_URL}/auth/access`,
      assignRoleBody,
    )
  }

  unassignRole(userRoleUuid: string) {
    return this.http.delete(
      `${this.AUTH_API_BASE_URL}/auth/access/${userRoleUuid}`,
    )
  }

  changeProtectionStatus(userRoleUuid: string, protectRole: boolean) {
    return this.http.put<H2HUserRole>(
      `${this.AUTH_API_BASE_URL}/auth/access/${userRoleUuid}`,
      {
        protected: protectRole,
      },
    )
  }

  getCurrentGeo() {
    return this.http.get<{
      latitude: string
      longitude: string
      city: string
      region: string
    }>(`https://public-api.wordpress.com/geo/`)
  }

  inviteUser(inviteUserBody: InviteUserBody) {
    return this.http.post<H2HUserRole>(
      `${this.AUTH_API_BASE_URL}/auth/invite`,
      inviteUserBody,
    )
  }

  login(body: LoginBody) {
    return this.http
      .post<LoginResponse>(`${this.AUTH_API_BASE_URL}/auth/login`, body)
      .pipe(
        concatMap((resp) => {
          localStorage.setItem('idToken', resp.idToken)
          localStorage.setItem('accessToken', resp.accessToken)
          if (resp.refreshToken) {
            localStorage.setItem('refreshToken', resp.refreshToken)
          }
          return this.updateUserRoles().pipe(
            map((roles) => {
              this.updateInterests()
              this.updateUser()
              this.updateUserCreatedAt() // Update creation date on login

              // Trigger expired pets check after user profile is loaded
              // Use a small delay to ensure all user data is properly loaded
              setTimeout(() => {
                this.triggerExpiredPetsCheck()
              }, 500)

              return roles
            }),
          )
        }),
      )
  }

  updateUserRoles() {
    return this.getMyUserRoles().pipe(
      tap((resp) => {
        localStorage.setItem('user', JSON.stringify(resp))
        this.updateIsAuthenticated()
        this.updateIsSuperAdmin()
        this.updateIsShelterAdmin()
        this.updateUser()
        this.updateUserCreatedAt() // Update creation date when roles are refreshed
      }),
      map((resp) => resp), // Return the response
    )
  }

  impersonate(userUuid: string) {
    return this.http
      .post<LoginResponse>(
        `${this.AUTH_API_BASE_URL}/auth/login`,
        { userUuid },
        { headers: { Authorization: `Bearer ${this.accessToken}` } },
      )
      .pipe(
        concatMap((resp) => {
          localStorage.setItem('IMP::idToken', resp.idToken)
          localStorage.setItem('IMP::accessToken', resp.accessToken)
          return this.getMyUserRoles().pipe(
            map((roles) => {
              localStorage.setItem('IMP::user', JSON.stringify(roles))
              this.updateIsSuperAdmin()
              this.updateIsShelterAdmin()
              this.updateVerified()
              this.updateInterests()
              this.updateUser()
              this.updateUserCreatedAt() // Update creation date on impersonation
              return roles
            }),
          )
        }),
      )
  }

  setTemporaryPassword(userUuid: string) {
    return this.http.post<TemporaryPasswordResponse>(
      `${this.AUTH_API_BASE_URL}/auth/temp-password/${userUuid}`,
      {},
      { headers: { Authorization: `Bearer ${this.accessToken}` } },
    )
  }

  changeUserStatus(email: string, banUser: boolean) {
    return this.http.put<H2HProfile>(
      `${this.AUTH_API_BASE_URL}/auth/access/status`,
      { email, banUser },
      { headers: { Authorization: `Bearer ${this.accessToken}` } },
    )
  }

  deactivateAccount() {
    return this.http.put(
      `${this.AUTH_API_BASE_URL}/v2/users/deactivate`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    )
  }

  forgotPassword(email: string) {
    return this.http.post(`${this.AUTH_API_BASE_URL}/auth/forgot-password`, {
      email: email,
    })
  }

  resetPassword(body: ForgotPasswordBody) {
    return this.http.post(`${this.AUTH_API_BASE_URL}/auth/reset-password`, body)
  }

  logout() {
    // Preserve non-auth user preferences across logout. localStorage.clear()
    // would otherwise wipe the translation-language choice (and any other
    // client-side prefs), so we save them before clearing and restore after.
    const preservedLang = localStorage.getItem('h2h.ada.lang')
    const preservedBarExpanded = localStorage.getItem('h2h.ada.barExpanded')
    const preservedScale = localStorage.getItem('h2h.ada.scale')
    const preservedContrast = localStorage.getItem('h2h.ada.contrast')
    const preservedMotion = localStorage.getItem('h2h.ada.motion')
    localStorage.clear()
    sessionStorage.clear()
    if (preservedLang) localStorage.setItem('h2h.ada.lang', preservedLang)
    if (preservedBarExpanded)
      localStorage.setItem('h2h.ada.barExpanded', preservedBarExpanded)
    if (preservedScale) localStorage.setItem('h2h.ada.scale', preservedScale)
    if (preservedContrast)
      localStorage.setItem('h2h.ada.contrast', preservedContrast)
    if (preservedMotion) localStorage.setItem('h2h.ada.motion', preservedMotion)
    this.updateIsAuthenticated()
    this.updateIsSuperAdmin()
    this.updateIsShelterAdmin()
    this.verified$.next(null)
    this.interests$.next(null)
    this.user$.next(null)
    this.userCreatedAt$.next(null) // Clear creation date on logout
    this.userProfileLoaded$.next(false) // Clear profile loaded status on logout
  }

  /**
   * Check if the current user has any expired pets
   * Returns all expired pets
   */
  checkForExpiredPets() {
    return this.http
      .get<H2HPet[]>(`${this.AUTH_API_BASE_URL}/pet/my?includeExpired=true`)
      .pipe(
        map((pets) => {
          const expiredPets = pets.filter((pet) => pet.status === 'EXPIRED')
          return expiredPets
        }),
      )
  }

  /**
   * Trigger expired pets check by dispatching a custom event
   * This allows the app component to handle the popup display
   */
  private triggerExpiredPetsCheck() {
    // Mark that user profile is loaded
    this.userProfileLoaded$.next(true)

    // Dispatch custom event to trigger expired pets check
    window.dispatchEvent(new CustomEvent('checkExpiredPets'))
  }

  unimpersonate() {
    localStorage.removeItem('IMP::accessToken')
    localStorage.removeItem('IMP::idToken')
    localStorage.removeItem('IMP::user')
    this.updateIsSuperAdmin()
    this.updateIsShelterAdmin()
    this.updateVerified()
    this.updateInterests()
    this.updateUser()
    this.updateUserCreatedAt() // Update creation date on unimpersonation
    this.router.navigate(['', 'account'])
  }

  searchRoles(searchRoleParams: SearchRolesParams) {
    return this.http
      .post<
        H2HProfileWithRole[]
      >(`${this.AUTH_API_BASE_URL}/auth/access/user`, searchRoleParams)
      .pipe(
        catchError((error) => {
          console.error('Error in searchRoles API call:', error)
          throw error
        }),
      )
  }

  getMyUserRoles() {
    return this.http.get<GetMyUserRolesResp>(
      `${this.AUTH_API_BASE_URL}/auth/access/user`,
    )
  }

  refreshIdToken(): Observable<LoginResponse | null> {
    const refreshToken = this.refreshToken
    if (!refreshToken) {
      return of(null)
    }

    return this.http
      .post<LoginResponse>(`${this.AUTH_API_BASE_URL}/auth/refresh`, {
        refreshToken,
      })
      .pipe(
        tap({
          next: (resp) => {
            localStorage.setItem('idToken', resp.idToken)
            localStorage.setItem('accessToken', resp.accessToken)
            if (resp.refreshToken) {
              localStorage.setItem('refreshToken', resp.refreshToken)
            }
            this.updateIsAuthenticated()
            this.updateIsSuperAdmin()
            this.updateIsShelterAdmin()
            this.updateVerified()
            this.updateInterests()
            this.updateUser()
            this.updateUserCreatedAt() // Update creation date on token refresh
          },
          error: (error) => {
            this.logout()
          },
        }),
      )
  }

  // Add a method to check if verification feature is enabled
  get isVerificationEnabled(): boolean {
    return environment.enableVerification
  }

  /**
   * Directly modifies the local ID token to set the verified status without making a server call
   * This is a temporary solution until the token is refreshed from the server
   * @param verificationStatus The verification status to set (can be true, session ID, or admin role)
   */
  setLocalVerificationStatus(verificationStatus: string): void {
    try {
      // Get the current token
      const token = this.IMPIdToken || this.idToken
      if (!token) {
        console.error('No ID token available to modify')
        return
      }

      // Create a JSON object with session and date
      const verificationData = {
        session: verificationStatus,
        date: new Date().toISOString(),
      }

      // Convert to JSON string
      const verificationJson = JSON.stringify(verificationData)

      // For simplicity, we're just updating the BehaviorSubject
      // This avoids complex token manipulation but serves the immediate UI need
      this.verified$.next(true)

      // Store the verification JSON in sessionStorage so it persists during the session
      // This will be used to override the token data until next login/refresh
      sessionStorage.setItem('h2h_verification_status', verificationJson)

      if (DEBUG)
        console.log(`Local verification status updated to: ${verificationJson}`)
    } catch (error) {
      console.error('Error updating local verification status:', error)
    }
  }

  /**
   * Set verification override for admin testing
   * @param override - true for verified, false for unverified, null to remove override
   */
  setVerificationOverride(override: boolean | null) {
    if (DEBUG)
      console.log('AuthService: Setting verification override to:', override)

    if (override === null) {
      localStorage.removeItem('admin_verification_override')
    } else {
      localStorage.setItem('admin_verification_override', override.toString())
    }

    // Update the verification status immediately
    this.updateVerified()

    if (DEBUG)
      console.log(
        'AuthService: Verification status after override:',
        this.verified$.value,
      )
  }

  /**
   * Gets the verification date from the custom:verified attribute
   * The attribute may be a string or a JSON array with a 'date' key
   * @returns Date object representing when the user was verified, or null if no date available
   */
  getVerificationDate(): Date | null {
    try {
      // First check Cognito token directly (without sessionStorage override)
      const token = this.IMPIdToken || this.idToken
      if (token) {
        const payload = this.decodeToken(token)
        const cognitoVerified = (payload as any)?.['custom:verified']

        if (cognitoVerified) {
          // Try to parse as JSON - might be a JSON array with date and session
          try {
            const verifiedData = JSON.parse(cognitoVerified)
            if (verifiedData && verifiedData.date) {
              return new Date(verifiedData.date)
            }
          } catch (e) {
            // Not JSON, just a string (old format or admin verification)
            // Don't return a date for string-based verification
          }
        }
      }

      // Only if Cognito doesn't have a value, fallback to sessionStorage
      // This handles the case where user just verified but Cognito token hasn't been updated yet
      const sessionData = sessionStorage.getItem('h2h_verification_status')
      if (sessionData) {
        try {
          const parsedSessionData = JSON.parse(sessionData)
          if (parsedSessionData && parsedSessionData.date) {
            return new Date(parsedSessionData.date)
          }
        } catch (e) {
          // Invalid session data, ignore
        }
      }

      // If we get here, no date is available from either source
      return null
    } catch (error) {
      console.error('Error parsing verification date:', error)
      return null
    }
  }
}

export type RegisterBody = {
  firstName: string
  lastName: string
  email: string
  password: string
  zipcode: string
  interests: (typeof USER_INTERESTS)[number]['value']
  newsletter: boolean
  fosterParent: boolean
  textMessageOptIn: boolean
  turnstileToken?: string
}

export type RegisterResponse = {
  userUuid: string
  confirmed: boolean
}

export type ConfirmBody = {
  email: string
  code: number
}

export type LoginResponse = {
  accessToken: string
  expiresIn: number
  idToken: string
  refreshToken?: string
}

export type TemporaryPasswordResponse = {
  password: string
}

export type LoginBody = {
  email: string
  password: string
  turnstileToken?: string
}

export type ForgotPasswordBody = {
  email: string
  password: string
  code: string
}

export type SearchRolesParams = {
  userUuid?: string
  roleId?: string
  resourceId?: string
}

export type GetMyUserRolesResp = {
  role: H2HRole
  user: H2HProfile
  userRole: H2HUserRole
}

export type AssignRoleBody = {
  userUuid?: string
  roleId?: string
  resourceId?: string
}

export interface InviteUserBody {
  email: string
  petUuid?: string
  reason: string
}
